import { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import { sqlRead, sqlReadRaw, payloadOf } from "../utils/sqlClient";
import { btnPrimary } from "../styles/buttons";
import { useIsAdmin, AdminGate } from "../auth/AdminContext";
import { LEARNING_DOMAINS, DEFAULT_LEARNING_TUNABLES, pct } from "../utils/learning/learningCore";
import { buildWallModel, proposeWallLearning, summarizeFillPerformance } from "../utils/learning/wallLearning";

// Setup > Learning — where the platform's engines learn from what estimators decide (framework:
// docs/implementation/LEARNING_SYSTEMS.md). The first live domain is wall construction: past human-decided walls
// become PROPOSALS here (rules for Construction Standards, wall systems for Room Templates); an admin approves each
// one before it exists, and approving applies it in one step. Nothing in this page changes an estimate by itself.
// AI-assisted draft: every proposal is a statistical summary of past estimator choices, not a verified standard —
// an authorized estimator reviews each one.

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "6px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12.5, verticalAlign: "top" };
const STATUS_STYLE = {
  proposed: { background: "var(--tint-blue)", color: "var(--cs-blue)" },
  approved: { background: "var(--tint-green)", color: "#166534" },
  rejected: { background: "var(--surface2)", color: "var(--text3)" },
  stale: { background: "var(--tint-amber)", color: "#92400e" },
};
const REL_WORD = { shared: "shared wall", cavity: "cavity face", exterior: "exterior wall" };
const parse = (s) => { try { return s ? JSON.parse(s) : null; } catch { return null; } };

function Badge({ status }) {
  return <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 9, textTransform: "uppercase", letterSpacing: "0.04em", ...(STATUS_STYLE[status] || {}) }}>{status}</span>;
}

function EvidenceTable({ evidence }) {
  if (!evidence?.groups?.length) return null;
  return (
    <table style={{ borderCollapse: "collapse", marginTop: 4 }}>
      <thead><tr>
        <th style={th}>Construction chosen</th><th style={{ ...th, textAlign: "right" }}>Walls</th><th style={{ ...th, textAlign: "right" }}>Projects</th>
        <th style={th}>Gap seen</th><th style={{ ...th, textAlign: "right" }}>Kept as filled</th><th style={{ ...th, textAlign: "right" }}>Changed to this</th>
      </tr></thead>
      <tbody>
        {evidence.groups.map(g => (
          <tr key={g.key}>
            <td style={td}>{g.name}</td>
            <td style={{ ...td, textAlign: "right" }}>{g.samples}</td>
            <td style={{ ...td, textAlign: "right" }}>{g.projects}</td>
            <td style={td}>{g.minGap == null ? "—" : `${+g.minGap}′ – ${+g.maxGap}′`}</td>
            <td style={{ ...td, textAlign: "right" }}>{g.confirmed ?? "—"}</td>
            <td style={{ ...td, textAlign: "right" }}>{g.corrected ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function Learning() {
  const isAdmin = useIsAdmin();
  const { accounts } = useMsal();
  const reviewer = accounts[0]?.username || null;

  const [proposals, setProposals] = useState([]);
  const [model, setModel] = useState(null);
  const [analysis, setAnalysis] = useState(null);   // { proposals, covered, conflicts, ambiguous, at }
  const [ctxData, setCtxData] = useState({ rules: [], templates: [], items: [], assemblies: [] });
  const [tunableRows, setTunableRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const tunables = useMemo(() => ({ ...DEFAULT_LEARNING_TUNABLES, ...Object.fromEntries(tunableRows.filter(r => r.Category === "wall_learning").map(r => [r.VariableKey, +r.Value])) }), [tunableRows]);

  const load = () => {
    setLoading(true); setError("");
    return Promise.all([
      sqlRead("EXEC sp_GetLearnedProposals @domain='wall_pair_rule'"),
      sqlRead("EXEC sp_GetLearnedProposals @domain='wall_template_position'"),
      sqlReadRaw("EXEC sp_GetWallLearningModel"),
      sqlRead("EXEC sp_GetConstructionStandardRules"),
      sqlReadRaw("EXEC sp_GetRoomTemplates"),
      sqlReadRaw("EXEC sp_GetEstimateCostLibrary"),
      sqlReadRaw("EXEC sp_GetTakeoffVariables"),
    ]).then(([pairProps, tplProps, m, rules, tpls, lib, vars]) => {
      setProposals([...(pairProps || []), ...(tplProps || [])]);
      setModel(buildWallModel({ pairRows: m?.Table1 || [], templateRows: m?.Table2 || [], performanceRows: m?.Table3 || [], totalsRow: m?.Table4?.[0] || null }));
      setCtxData({ rules: rules || [], templates: tpls?.Table1 || [], assemblies: lib?.Table2 || [], items: lib?.Table3 || [] });
      setTunableRows(vars?.Table1 || []);
    }).catch(e => setError(e.message || String(e))).finally(() => setLoading(false));
  };
  // NOT useEffect(load, []) — load() returns a Promise (see ConstructionStandards.jsx).
  useEffect(() => { load(); }, []);

  const entityName = (kind, id) => {
    if (kind === "item") { const i = ctxData.items.find(x => x.ItemID === id); return i ? `${i.Code || ""} ${i.ItemName || ""}`.trim() : `item ${id}`; }
    const a = ctxData.assemblies.find(x => x.AssemblyID === id);
    return a ? `${a.Code || ""} ${a.Description || ""}`.trim() : `assembly ${id}`;
  };
  const templateName = (id) => ctxData.templates.find(t => t.RoomTemplateID === id)?.Name || `template ${id}`;

  async function analyze() {
    if (!model) return;
    setBusy(true); setError("");
    try {
      const result = proposeWallLearning(model, { rules: ctxData.rules, templates: ctxData.templates, entityName, templateName }, tunables);
      for (const domain of ["wall_pair_rule", "wall_template_position"]) {
        const mine = result.proposals.filter(p => p.domain === domain);
        const payload = payloadOf({
          domain, mark_missing_stale: 1,
          proposals: mine.map(p => ({ key: p.key, title: p.title, proposal: p.proposal, evidence: p.evidence, samples: p.samples, projects: p.projects, agreement: p.agreement })),
        });
        const res = (await sqlRead(`EXEC sp_SaveLearnedProposals @payload = '${payload}'`))?.[0];
        if (res && res.Success === 0) throw new Error(res.ErrorMessage);
      }
      setAnalysis({ ...result, at: new Date() });
      await load();
    } catch (e) { setError("Analysis failed: " + (e.message || e)); } finally { setBusy(false); }
  }

  async function review(p, decision) {
    let note = null;
    if (decision === "reject") { note = window.prompt("Why reject this? (optional — it stays rejected and is not proposed again)", "") ; if (note === null) return; }
    if (decision === "approve" && !window.confirm(`Approve and apply this now?\n\n${p.Title}\n\nIt becomes ${p.Domain === "wall_pair_rule" ? "a Construction Standards rule (priority 100)" : "a wall system on the Room Template"}. Walls already assigned are never changed; blank walls start using it.`)) return;
    setBusy(true); setError("");
    try {
      const payload = payloadOf({ proposal_id: p.ProposalID, decision, note, reviewed_by: reviewer });
      const res = (await sqlRead(`EXEC sp_ReviewLearnedProposal @payload = '${payload}'`))?.[0];
      if (res && res.Success === 0) { setError(res.ErrorMessage); return; }
      await load();
    } catch (e) { setError("Could not save the decision: " + (e.message || e)); } finally { setBusy(false); }
  }

  const perf = useMemo(() => summarizeFillPerformance(model?.performance, tunables), [model, tunables]);
  const ruleName = (id) => { const r = ctxData.rules.find(x => x.RuleID === id); return r ? `${r.RoomTemplateAName} ↔ ${r.RoomTemplateBName}${r.RelationshipType ? ` (${REL_WORD[r.RelationshipType]})` : ""}` : `rule ${id}`; };
  const open = proposals.filter(p => p.Status === "proposed" || p.Status === "stale");
  const done = proposals.filter(p => p.Status === "approved" || p.Status === "rejected");

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  const T = model?.totals || { decisions: 0, evidenceDecisions: 0, projects: 0 };

  const Row = ({ p }) => {
    const ev = parse(p.EvidenceJson);
    return (
      <>
        <tr style={{ opacity: p.Status === "rejected" ? 0.6 : 1 }}>
          <td style={td}><button onClick={() => setOpenId(openId === p.ProposalID ? null : p.ProposalID)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text)", textAlign: "left", padding: 0, fontSize: 12.5 }}>{openId === p.ProposalID ? "▾" : "▸"} {p.Title}</button></td>
          <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{p.Samples}</td>
          <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{p.Projects}</td>
          <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{p.Agreement == null ? "—" : pct(+p.Agreement)}</td>
          <td style={td}><Badge status={p.Status} />{p.AppliedRef ? <span style={{ marginLeft: 6, fontSize: 11, color: "var(--text3)" }}>{p.AppliedRef}</span> : null}</td>
          <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
            {p.Status === "proposed" && <>
              <button disabled={busy} onClick={() => review(p, "approve")} style={{ ...btnPrimary, fontSize: 11, padding: "3px 9px", marginRight: 6 }}>Approve</button>
              <button disabled={busy} onClick={() => review(p, "reject")} style={{ fontSize: 11, cursor: "pointer", color: "#b91c1c" }}>Reject</button>
            </>}
            {p.Status === "stale" && <button disabled={busy} onClick={() => review(p, "reject")} style={{ fontSize: 11, cursor: "pointer" }}>Dismiss</button>}
            {p.Status === "rejected" && <button disabled={busy} onClick={() => review(p, "reopen")} style={{ fontSize: 11, cursor: "pointer" }}>Reopen</button>}
          </td>
        </tr>
        {openId === p.ProposalID && (
          <tr><td colSpan={6} style={{ ...td, background: "var(--surface2)" }}>
            {p.Status === "stale" && <div style={{ color: "#92400e", marginBottom: 4 }}>The history has moved since this was proposed — re-run the analysis; it is no longer produced by the current evidence.</div>}
            <EvidenceTable evidence={ev} />
            {p.ReviewNote && <div style={{ marginTop: 4, color: "var(--text3)" }}>Note: {p.ReviewNote}{p.ReviewedBy ? ` — ${p.ReviewedBy}` : ""}</div>}
          </td></tr>
        )}
      </>
    );
  };

  const Table = ({ rows }) => (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr>
        <th style={th}>Proposal</th><th style={{ ...th, textAlign: "right" }}>Walls</th><th style={{ ...th, textAlign: "right" }}>Projects</th>
        <th style={{ ...th, textAlign: "right" }}>Agreement</th><th style={th}>Status</th><th style={th}></th>
      </tr></thead>
      <tbody>{rows.map(p => <Row key={p.ProposalID} p={p} />)}</tbody>
    </table>
  );

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1180 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Learning</h2>
      <div style={{ fontSize: 12.5, color: "var(--text3)", marginBottom: 6, maxWidth: 720 }}>
        The takeoff engines learn from what estimators decide. Only walls a person <b>accepted or corrected</b> count as evidence — the engine never learns
        from its own unreviewed output. What it learns is shown here as <b>proposals</b> an admin approves; nothing becomes a rule on its own.
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 16 }}>AI-assisted draft — proposals summarize past estimator choices and need estimator review before they feed a bid.</div>

      {error && <div style={{ color: "#b91c1c", fontSize: 12.5, marginBottom: 10 }}>{error}</div>}

      <h3 style={{ fontSize: 14, margin: "0 0 6px" }}>Wall construction <span style={{ fontSize: 11, fontWeight: 600, color: "#166534" }}>· live</span></h3>
      {loading ? <div style={{ fontSize: 12.5, color: "var(--text3)" }}>Loading…</div> : (
        <>
          <div style={{ fontSize: 12.5, color: "var(--text2)", marginBottom: 8 }}>
            Evidence on file: <b>{T.evidenceDecisions}</b> human-decided wall{T.evidenceDecisions === 1 ? "" : "s"} from <b>{T.projects}</b> project{T.projects === 1 ? "" : "s"}
            {T.decisions > T.evidenceDecisions ? ` (${T.decisions - T.evidenceDecisions} accepted learned pre-fills are not counted — they add no new evidence)` : ""}.
            A rule needs <b>{tunables.LEARN_MIN_SAMPLES}</b> walls, <b>{tunables.LEARN_MIN_PROJECTS}</b> project(s) and <b>{pct(tunables.LEARN_MIN_AGREEMENT)}</b> agreement; a blank wall is pre-filled
            {+tunables.LEARN_PREDICT_ENABLED > 0 ? <> from <b>{tunables.LEARN_PREDICT_MIN_SAMPLES}</b> walls at <b>{pct(tunables.LEARN_PREDICT_MIN_AGREEMENT)}</b></> : <> <b>never</b> (switched off)</>}.
            Thresholds: <Link to="/estimate-engine">Estimate Engine ▸ Engine Variables</Link>.
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button disabled={busy} onClick={analyze} style={btnPrimary}>{busy ? "Working…" : "Analyze history"}</button>
            <button disabled={busy} onClick={load} style={{ fontSize: 12, cursor: "pointer" }}>Refresh</button>
          </div>

          {analysis && (
            <div style={{ fontSize: 12.5, color: "var(--text2)", border: "1px solid var(--cs-border)", borderRadius: 6, padding: "8px 10px", marginBottom: 12, background: "var(--surface2)" }}>
              <b>Analysis {analysis.at.toLocaleTimeString()}</b>: {analysis.proposals.length} proposal{analysis.proposals.length === 1 ? "" : "s"}, {analysis.covered} already configured the same way,
              {" "}{analysis.conflicts.length} disagreeing with a configured rule, {analysis.ambiguous.length} without a clear winner.
              {analysis.conflicts.length > 0 && (
                <div style={{ marginTop: 6 }}><b>History disagrees with what is configured</b> (nothing was changed — review the rule):
                  <ul style={{ margin: "2px 0 0 18px" }}>{analysis.conflicts.map((c, i) => (
                    <li key={i}>{c.kind === "pair" ? `${templateName(c.lo)} ↔ ${templateName(c.hi)}, ${REL_WORD[c.rel]}` : `${templateName(c.templateId)}, ${REL_WORD[c.rel]}`}: people chose <b>{c.proposedName}</b> ({pct(c.agreement)} of {c.total} walls), but {c.rules.map(r => r.ruleId ? `rule #${r.ruleId} (${r.name})` : r.name).join(", ")} says otherwise.</li>
                  ))}</ul>
                </div>
              )}
              {analysis.ambiguous.length > 0 && (
                <div style={{ marginTop: 6 }}><b>No clear winner</b> (a human decides the rule — this is the real standard-setting question):
                  <ul style={{ margin: "2px 0 0 18px" }}>{analysis.ambiguous.map((a, i) => (
                    <li key={i}>{a.kind === "pair" ? `${templateName(a.lo)} ↔ ${templateName(a.hi)}, ${REL_WORD[a.rel]}` : `${templateName(a.templateId)}, ${REL_WORD[a.rel]}`}: {a.reason === "split" ? "history is split" : "not enough projects yet"} — {a.groups.map(g => `${g.name} ×${g.samples}`).join(", ")}</li>
                  ))}</ul>
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "4px 0" }}>Awaiting a decision ({open.length})</div>
          {open.length ? <Table rows={open} /> : <div style={{ fontSize: 12.5, color: "var(--text3)", marginBottom: 8 }}>Nothing proposed yet. Proposals appear once enough walls have been accepted or corrected on real estimates — then press Analyze history.</div>}

          {done.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setShowDone(v => !v)} style={{ fontSize: 12, cursor: "pointer", marginBottom: 6 }}>{showDone ? "Hide" : "Show"} decided ({done.length})</button>
              {showDone && <Table rows={done} />}
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "18px 0 4px" }}>How often people keep what the engine filled</div>
          {perf.length ? (
            <table style={{ borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Filled by</th><th style={{ ...th, textAlign: "right" }}>Kept</th><th style={{ ...th, textAlign: "right" }}>Changed</th><th style={{ ...th, textAlign: "right" }}>Kept rate</th><th style={th}></th></tr></thead>
              <tbody>{perf.map((p, i) => (
                <tr key={i}>
                  <td style={td}>{p.source === "rule" ? `Rule — ${ruleName(p.ruleId)}` : p.source === "wall_system" ? "Template / room wall system" : "Learned pre-fill"}</td>
                  <td style={{ ...td, textAlign: "right" }}>{p.accepted}</td><td style={{ ...td, textAlign: "right" }}>{p.corrected}</td><td style={{ ...td, textAlign: "right" }}>{p.keptText}</td>
                  <td style={{ ...td, color: "#b45309" }}>{p.underperforming ? "People keep changing this — review it" : ""}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <div style={{ fontSize: 12.5, color: "var(--text3)" }}>No filled walls have been reviewed yet.</div>}
        </>
      )}

      <h3 style={{ fontSize: 14, margin: "26px 0 6px" }}>Other systems to learn from</h3>
      <div style={{ fontSize: 12.5, color: "var(--text3)", marginBottom: 8, maxWidth: 720 }}>
        Each follows the same loop: capture what the engine knew and chose, stamp the person's accept / correct, aggregate human decisions only, propose, approve.
        Planned systems record what they need as the features are built so the history exists when their screen does.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={th}>System</th><th style={th}>Learns</th><th style={th}>Signal</th><th style={th}>Output</th><th style={th}>Status</th></tr></thead>
        <tbody>{LEARNING_DOMAINS.map(d => (
          <tr key={d.id}><td style={{ ...td, fontWeight: 600 }}>{d.label}</td><td style={td}>{d.learns}</td><td style={td}>{d.signal}</td><td style={td}>{d.output}</td>
            <td style={td}><span style={{ fontSize: 11, fontWeight: 700, color: d.status === "live" ? "#166534" : "var(--text3)" }}>{d.status}</span></td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}
